import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Volume1,
  Heart, Maximize2, Lock, Clock, Shuffle, Repeat, Repeat1,
  Download, Settings, ListMusic
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePlayer } from '../context/PlayerContext';
import { PlayerExpanded } from './PlayerExpanded';
import { SubscriptionPromptModal } from './SubscriptionPromptModal';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export const Player: React.FC = () => {
  const { state, dispatch, audioRef } = usePlayer();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [prevVolume, setPrevVolume] = useState(0.7);
  const [showQuality, setShowQuality] = useState(false);
  const [quality, setQuality] = useState<'high' | 'medium' | 'low'>('high');
  const [shuffle, setShuffle] = useState(false);
  const [repeatMode, setRepeatMode] = useState<'off' | 'all' | 'one'>('off');
  const [expanded, setExpanded] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);
  const qualityRef = useRef<HTMLDivElement>(null);

  const isPreviewMode = state.isPreviewMode && !state.hasFullAccess && !state.currentTrack?.isPurchased;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      if (audio.buffered.length > 0) {
        setBuffered(audio.buffered.end(audio.buffered.length - 1));
      }
    };
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('progress', updateTime);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('progress', updateTime);
    };
  }, [audioRef]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (qualityRef.current && !qualityRef.current.contains(e.target as Node)) {
        setShowQuality(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !audioRef.current || !state.currentTrack) return;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    let newTime = percent * state.currentTrack.duration;

    if (isPreviewMode && newTime > 30) {
      newTime = 30;
      toast.info('Subscribe or purchase to listen to the full track');
    }

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleSeek = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handlePurchase = () => {
    dispatch({ type: 'SHOW_SUBSCRIPTION_PROMPT', payload: false });
    navigate('/subscription', { state: { purchaseTrack: state.currentTrack } });
  };

  const handleClosePrompt = () => {
    dispatch({ type: 'SHOW_SUBSCRIPTION_PROMPT', payload: false });
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolumeValue(newVolume);
  };

  const setVolumeValue = (newVolume: number) => {
    setVolume(newVolume);
    dispatch({ type: 'SET_VOLUME', payload: newVolume });
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const toggleMute = () => {
    if (volume > 0) {
      setPrevVolume(volume);
      setVolumeValue(0);
    } else {
      setVolumeValue(prevVolume);
    }
  };

  const cycleRepeat = () => {
    const modes: Array<'off' | 'all' | 'one'> = ['off', 'all', 'one'];
    const idx = modes.indexOf(repeatMode);
    setRepeatMode(modes[(idx + 1) % 3]);
  };

  const handleDownload = useCallback(() => {
    if (!state.currentTrack) return;
    if (isPreviewMode) {
      toast.error('Subscribe or purchase to download this track');
      return;
    }
    const url = state.currentTrack.audioUrl || state.currentTrack.audio_url;
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = `${state.currentTrack.title}.mp3`;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Download started');
    }
  }, [state.currentTrack, isPreviewMode]);

  const VolumeIcon = volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
  const duration = state.currentTrack?.duration || 0;
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferedPercent = duration > 0 ? (buffered / duration) * 100 : 0;

  if (!state.currentTrack) return null;

  return (
    <>
    <div className="fixed bottom-0 left-0 right-0 z-40 glass-royal backdrop-blur-royal">
      <div
        ref={progressRef}
        onClick={handleProgressClick}
        className="h-1.5 bg-royal-border cursor-pointer group relative overflow-hidden hover:h-2.5 transition-all"
      >
        <div
          className="absolute top-0 left-0 h-full bg-white/10 transition-all"
          style={{ width: `${bufferedPercent}%` }}
        />
        <div
          className="absolute top-0 left-0 h-full bg-royal-gold transition-all z-10"
          style={{ width: `${progressPercent}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-royal opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        {isPreviewMode && (
          <div
            className="absolute top-0 h-full bg-red-500/20 z-20 pointer-events-none"
            style={{ left: `${(30 / duration) * 100}%`, right: 0 }}
          >
            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-red-500/60" />
          </div>
        )}
      </div>

      <div className="px-4 md:px-6 py-3 md:py-4">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between gap-4 md:gap-8">
            <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0 max-w-xs md:max-w-sm">
              <div
                className="relative group/cover flex-shrink-0 cursor-pointer"
                onClick={() => setExpanded(true)}
              >
                <img
                  src={state.currentTrack.cover_art_url || 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=100&q=80'}
                  alt={state.currentTrack.title}
                  className="w-12 h-12 md:w-14 md:h-14 rounded-xl object-cover shadow-royal"
                />
                <div className="absolute inset-0 rounded-xl bg-black/40 opacity-0 group-hover/cover:opacity-100 transition-opacity flex items-center justify-center">
                  <Maximize2 size={16} className="text-white" />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-royal-text truncate">
                    {state.currentTrack.title}
                  </h4>
                  {isPreviewMode && (
                    <span className="hidden sm:flex items-center gap-1 px-2 py-0.5 bg-royal-gold/10 text-royal-gold rounded-full text-xs font-medium flex-shrink-0">
                      <Lock className="w-3 h-3" />
                      Preview
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-royal-text-secondary truncate">
                    {state.currentTrack.artist_name || 'Unknown Artist'}
                  </p>
                  {isPreviewMode && state.previewTimeRemaining > 0 && (
                    <span className="hidden sm:flex items-center gap-1 text-xs text-royal-gold font-medium flex-shrink-0">
                      <Clock className="w-3 h-3" />
                      {Math.ceil(state.previewTimeRemaining)}s
                    </span>
                  )}
                </div>
              </div>
              <button className="p-2 hover:bg-royal-hover rounded-full transition-all group flex-shrink-0 hidden sm:block">
                <Heart size={18} className="text-royal-text-secondary group-hover:text-royal-gold group-hover:fill-royal-gold transition-all" strokeWidth={2} />
              </button>
            </div>

            <div className="flex flex-col items-center gap-1.5 md:gap-2 flex-1 max-w-2xl">
              <div className="flex items-center gap-3 md:gap-5">
                <button
                  onClick={() => setShuffle(!shuffle)}
                  className={`p-1.5 rounded-full transition-all hidden md:block ${shuffle ? 'text-royal-gold' : 'text-royal-text-secondary hover:text-royal-text'}`}
                >
                  <Shuffle size={16} strokeWidth={2} />
                </button>

                <button
                  onClick={() => dispatch({ type: 'PREVIOUS_TRACK' })}
                  className="p-2 hover:bg-royal-hover rounded-full transition-all hover:scale-110 active:scale-95"
                >
                  <SkipBack size={18} className="text-royal-text" strokeWidth={2.5} />
                </button>

                <button
                  onClick={() => dispatch({ type: 'TOGGLE_PLAY' })}
                  className="p-3 md:p-4 bg-royal-gold text-white rounded-full hover:bg-royal-gold-dark hover:scale-110 active:scale-95 transition-all shadow-royal"
                >
                  {state.isPlaying ? (
                    <Pause size={18} fill="currentColor" />
                  ) : (
                    <Play size={18} fill="currentColor" className="ml-0.5" />
                  )}
                </button>

                <button
                  onClick={() => dispatch({ type: 'NEXT_TRACK' })}
                  className="p-2 hover:bg-royal-hover rounded-full transition-all hover:scale-110 active:scale-95"
                >
                  <SkipForward size={18} className="text-royal-text" strokeWidth={2.5} />
                </button>

                <button
                  onClick={cycleRepeat}
                  className={`p-1.5 rounded-full transition-all hidden md:block ${repeatMode !== 'off' ? 'text-royal-gold' : 'text-royal-text-secondary hover:text-royal-text'}`}
                >
                  {repeatMode === 'one' ? <Repeat1 size={16} strokeWidth={2} /> : <Repeat size={16} strokeWidth={2} />}
                </button>
              </div>

              <div className="w-full flex items-center gap-2 md:gap-3">
                <span className="text-[10px] md:text-xs text-royal-text-secondary w-10 md:w-12 text-right tabular-nums font-medium">
                  {formatTime(currentTime)}
                </span>
                <div className="flex-1 h-1 md:h-1.5 bg-royal-border rounded-full overflow-hidden relative">
                  <div
                    className="absolute top-0 left-0 h-full bg-white/10 rounded-full"
                    style={{ width: `${bufferedPercent}%` }}
                  />
                  <div
                    className="absolute top-0 left-0 h-full bg-royal-gold rounded-full transition-all z-10"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="text-[10px] md:text-xs text-royal-text-secondary w-10 md:w-12 tabular-nums font-medium">
                  {formatTime(duration)}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-3 flex-1 justify-end max-w-sm">
              <button
                onClick={() => setExpanded(true)}
                className="p-1.5 hover:bg-royal-hover rounded-full transition-all text-royal-text-secondary hover:text-royal-text hidden md:block"
                title="Expand player"
              >
                <Maximize2 size={16} strokeWidth={2} />
              </button>

              {state.queue.length > 0 && (
                <button
                  onClick={() => setExpanded(true)}
                  className="p-1.5 hover:bg-royal-hover rounded-full transition-all text-royal-text-secondary hover:text-royal-text hidden md:flex items-center gap-1"
                  title="Queue"
                >
                  <ListMusic size={16} strokeWidth={2} />
                  <span className="text-[10px] font-bold bg-royal-gold text-white rounded-full w-4 h-4 flex items-center justify-center">
                    {state.queue.length}
                  </span>
                </button>
              )}

              <div className="relative hidden md:block" ref={qualityRef}>
                <button
                  onClick={() => setShowQuality(!showQuality)}
                  className="p-1.5 hover:bg-royal-hover rounded-full transition-all text-royal-text-secondary hover:text-royal-text"
                >
                  <Settings size={16} strokeWidth={2} />
                </button>
                {showQuality && (
                  <div className="absolute bottom-full right-0 mb-2 bg-white border border-stone-200 rounded-xl shadow-xl py-1 min-w-[160px] z-50">
                    <div className="px-3 py-1.5 text-xs font-medium text-royal-text-secondary uppercase tracking-wider">Quality</div>
                    {(['high', 'medium', 'low'] as const).map(q => (
                      <button
                        key={q}
                        onClick={() => { setQuality(q); setShowQuality(false); }}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-stone-50 ${quality === q ? 'text-royal-gold font-medium' : 'text-royal-text'}`}
                      >
                        <span className="capitalize">{q}</span>
                        <span className="text-xs text-royal-text-secondary">
                          {q === 'high' ? '320kbps' : q === 'medium' ? '160kbps' : '96kbps'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={handleDownload}
                className="p-1.5 hover:bg-royal-hover rounded-full transition-all text-royal-text-secondary hover:text-royal-text hidden md:block"
                title={isPreviewMode ? 'Subscribe to download' : 'Download'}
              >
                <Download size={16} strokeWidth={2} />
              </button>

              <button
                onClick={toggleMute}
                className="p-1.5 hover:bg-royal-hover rounded-full transition-all flex-shrink-0 hidden md:block"
              >
                <VolumeIcon size={16} className="text-royal-text-secondary" strokeWidth={2} />
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                className="w-20 h-1 bg-royal-border rounded-full appearance-none cursor-pointer hidden md:block
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-royal-gold
                  [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:transition-transform
                  [&::-webkit-slider-thumb]:hover:scale-125 [&::-webkit-slider-thumb]:shadow-royal"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <PlayerExpanded
      isOpen={expanded}
      onClose={() => setExpanded(false)}
      currentTime={currentTime}
      volume={volume}
      onVolumeChange={setVolumeValue}
      onSeek={handleSeek}
      shuffle={shuffle}
      onShuffleToggle={() => setShuffle(!shuffle)}
      repeatMode={repeatMode}
      onRepeatCycle={cycleRepeat}
    />

    <SubscriptionPromptModal
      isOpen={state.showSubscriptionPrompt}
      onClose={handleClosePrompt}
      contentType="song"
      contentTitle={state.currentTrack?.title || ''}
      contentArtist={state.currentTrack?.artist_name}
      contentPrice={state.currentTrack?.price || 0.99}
      contentCover={state.currentTrack?.cover_art_url}
      onPurchase={handlePurchase}
    />
    </>
  );
};
